import { useState, useCallback, useRef, useEffect } from 'react';
import type { TimerConfig, TimerState } from 'model';
import { playSoundOption } from 'utils/audio';

export interface TimerEvents {
  onWarning?: () => void;
  onSetEnd?: () => void;
  onRestEnd?: () => void;
  onComplete?: () => void;
}

interface UseTimerReturn {
  state: TimerState;
  start: () => void;
  pause: () => void;
  reset: () => void;
}

const initialState: TimerState = {
  phase: 'idle',
  timeRemaining: 0,
  currentSet: 0,
  isRunning: false,
  isPaused: false
};

export function useTimer(config: TimerConfig, events: TimerEvents = {}): UseTimerReturn {
  const [state, setState] = useState<TimerState>(initialState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningFiredRef = useRef<boolean>(false);

  // Store events in refs to avoid dependency issues
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Store config in ref for stable access in callbacks
  const configRef = useRef(config);
  configRef.current = config;

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const transitionToNextPhase = useCallback((currentState: TimerState): TimerState => {
    const { phase, currentSet } = currentState;
    const cfg = configRef.current;
    const evt = eventsRef.current;

    if (phase === 'work') {
      // Work phase ended, transition to rest
      evt.onSetEnd?.();

      // Check if all sets are complete (skip if totalSets is -1 for infinite)
      if (cfg.totalSets !== -1 && cfg.totalSets && currentSet >= cfg.totalSets) {
        evt.onComplete?.();
        return {
          ...currentState,
          phase: 'complete',
          timeRemaining: 0,
          isRunning: false,
          isPaused: false
        };
      }

      // If no interval duration, go directly to next work phase
      if (cfg.intervalDuration <= 0) {
        warningFiredRef.current = false;
        return {
          ...currentState,
          phase: 'work',
          timeRemaining: cfg.setDuration,
          currentSet: currentSet + 1
        };
      }

      return {
        ...currentState,
        phase: 'rest',
        timeRemaining: cfg.intervalDuration
      };
    }

    if (phase === 'rest') {
      // Rest phase ended, start new work phase
      evt.onRestEnd?.();
      warningFiredRef.current = false;

      return {
        ...currentState,
        phase: 'work',
        timeRemaining: cfg.setDuration,
        currentSet: currentSet + 1
      };
    }

    return currentState;
  }, []);

  const tick = useCallback(() => {
    setState((prevState) => {
      if (!prevState.isRunning || prevState.isPaused) {
        return prevState;
      }

      const newTimeRemaining = prevState.timeRemaining - 1;

      // Fire warning if applicable (only during work phase)
      if (
        prevState.phase === 'work' &&
        !warningFiredRef.current &&
        newTimeRemaining === configRef.current.warningTime &&
        newTimeRemaining > 0
      ) {
        warningFiredRef.current = true;
        eventsRef.current.onWarning?.();
      }

      // Phase complete, transition
      if (newTimeRemaining <= 0) {
        return transitionToNextPhase(prevState);
      }

      return {
        ...prevState,
        timeRemaining: newTimeRemaining
      };
    });
  }, [transitionToNextPhase]);

  // Set up interval when running
  useEffect(() => {
    if (state.isRunning && !state.isPaused) {
      intervalRef.current = setInterval(tick, 1000);
    } else {
      clearTimer();
    }

    return clearTimer;
  }, [state.isRunning, state.isPaused, tick, clearTimer]);

  const start = useCallback(() => {
    setState((prevState) => {
      // If paused, resume
      if (prevState.isPaused) {
        return {
          ...prevState,
          isPaused: false,
          isRunning: true
        };
      }

      // If idle or complete, start fresh
      if (prevState.phase === 'idle' || prevState.phase === 'complete') {
        warningFiredRef.current = false;
        return {
          phase: 'work',
          timeRemaining: configRef.current.setDuration,
          currentSet: 1,
          isRunning: true,
          isPaused: false
        };
      }

      // Otherwise just ensure running
      return {
        ...prevState,
        isRunning: true,
        isPaused: false
      };
    });
  }, []);

  const pause = useCallback(() => {
    setState((prevState) => {
      if (!prevState.isRunning || prevState.phase === 'idle' || prevState.phase === 'complete') {
        return prevState;
      }

      return {
        ...prevState,
        isPaused: true
      };
    });
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    warningFiredRef.current = false;
    setState(initialState);
  }, [clearTimer]);

  return {
    state,
    start,
    pause,
    reset
  };
}
